/**
 * Cloudinary Image URL Builder
 * Generates optimized image URLs with automatic format selection and quality optimization
 */

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'demo';
const BASE_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload`;

interface CloudinaryOptions {
  width?: number;
  height?: number;
  quality?: 'auto' | number;
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
  crop?: 'fill' | 'fit' | 'scale' | 'limit';
}

/**
 * Generate a Cloudinary URL for an image
 * @param publicId - The image path/filename in Cloudinary (e.g., "gallery/image.jpg")
 * @param options - Transformation options
 */
export function getCloudinaryUrl(publicId: string, options: CloudinaryOptions = {}): string {
  const {
    width,
    height,
    quality = 'auto',
    format = 'auto',
    crop = 'limit',
  } = options;

  const transformations: string[] = [];

  // Add dimensions
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  if (width || height) transformations.push(`c_${crop}`);

  // Add quality and format
  transformations.push(`q_${quality}`);
  transformations.push(`f_${format}`);

  const transformString = transformations.length > 0 ? `/${transformations.join(',')}` : '';

  // URL encode the public ID to handle spaces and special characters
  const encodedPublicId = encodeURIComponent(publicId)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');

  return `${BASE_URL}${transformString}/${encodedPublicId}`;
}

/**
 * Get a responsive srcset for an image
 */
export function getCloudinarySrcSet(publicId: string): string {
  const widths = [400, 800, 1200, 1600];
  return widths
    .map(width => `${getCloudinaryUrl(publicId, { width, quality: 'auto' })} ${width}w`)
    .join(', ');
}
